import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';
import './antd.css';
import { configureI18Next } from './configureI18Next';
import configureZod from './configureZod';
import {
    AGENT_API_URL,
    API_HOST,
    ENVIRONMENT,
    IS_LIVE_ENVIRONMENT,
    RELEASE_VERSION,
    SENTRY_DSN,
} from './constants/environment';
import './index.css';
import { ensureAnonymousIdCookie } from './lib/analytics/anonymousId';
import { App } from './routes/App';
import { shouldDropSentryEvent } from './sentryEventFilter';

if (IS_LIVE_ENVIRONMENT) {
    datadogRum.init({
        applicationId: '15baf5ce-527c-4fca-a5bf-317cb8836801',
        clientToken: 'pub377b7334467bf34f1fd9bb0d1a2072a4',
        site: 'datadoghq.com',
        service: 'web-app',
        env: ENVIRONMENT,
        version: RELEASE_VERSION,
        useSecureSessionCookie: true,
        trackSessionAcrossSubdomains: true,
        sessionSampleRate: ENVIRONMENT == 'staging' ? 100 : 0, // test in staging first
        sessionReplaySampleRate: 0, // Disable Session Replay
        trackUserInteractions: ENVIRONMENT == 'staging', // test in staging first
    });
}

const initSentry = () => {
    if (ENVIRONMENT !== 'production') {
        return;
    }

    const tracePropagationTargets = Array.from(
        new Set(
            [window.location.origin, API_HOST, AGENT_API_URL].filter((value): value is string =>
                Boolean(value),
            ),
        ),
    );
    Sentry.init({
        release: RELEASE_VERSION,
        dsn: SENTRY_DSN,
        environment: ENVIRONMENT,
        debug: false,
        sendDefaultPii: false,
        tracesSampleRate: 0.05,
        tracePropagationTargets,
        integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.captureConsoleIntegration({
                levels: ['error'],
            }),
        ],
        beforeSend(event, hint) {
            if (shouldDropSentryEvent(event, hint, API_HOST)) {
                return null;
            }
            return event;
        },
    });
};

const renderApp = () => {
    const container = document.getElementById('root');
    const root = createRoot(container!);
    root.render(
        <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
            <App />
        </Sentry.ErrorBoundary>,
    );
};

const bootstrap = async () => {
    // Restore the shared anonymous-id cookie that segment_anonymous_id_middleware set
    // server-side (gone once the SPA is served statically). Runs before Segment loads.
    // Analytics-only — never let it block app boot.
    try {
        ensureAnonymousIdCookie();
    } catch (error) {
        console.error('Failed to set anonymous id cookie', error);
    }

    // Configure i18n first, then Zod (Zod's error map uses i18n.t()).
    await configureI18Next();
    configureZod();

    try {
        initSentry();
    } catch (error) {
        console.error('Sentry was not set up correctly', error);
    }

    renderApp();
};

void bootstrap();
